/**
 * <PauseDatesStep> — l'étape « Quelles dates ? » du parcours de pause (organism).
 *
 * Le corps de la première étape du legacy (`ParentPauseSubscriptionModal.js:399-617`) : les deux
 * dates, la durée qui en découle, le refus quand il y en a un, et le motif. Le solde de pause du
 * legacy, en tête, n'y est plus (Wajih, 30/09) : le nombre de jours restants poussait aux pauses
 * longues, alors que la limite n'est qu'un garde-fou — les bornes des calendriers et le refus la
 * tiennent.
 *
 * Contrôlé de bout en bout : les dates, le motif et le refus arrivent en props. **Aucune règle
 * ici** — ni quota, ni durée minimale, ni fenêtre estivale : le view-model calcule, et n'envoie
 * qu'une phrase de refus (`dateError`) et les bornes des deux calendriers. Une phrase absente
 * retire la ligne, comme partout dans l'espace parent.
 *
 * La date de reprise attend la date de début, exactement comme le legacy : sans début, le champ
 * est éteint — ce n'est pas une règle métier, c'est l'ordre des deux questions.
 *
 * Quand le parent atteint une limite de l'app (la reprise au plus loin, une nouvelle pause qui
 * attend), une phrase courte le dit en tête (`limitNote`), avec le lien vers l'aide : au-delà, la
 * pause passe par l'équipe. Sans limite atteinte, rien ne s'affiche.
 */
import React from "react"

import { Banner } from "../../atoms/Banner"
import { AlertCard } from "../../molecules/AlertCard"
import { ChoiceChip } from "../../atoms/ChoiceChip"
import { Stack } from "../../atoms/containers/Stack"
import { TextButton } from "../../atoms/Buttons/TextButton"
import { Text } from "../../atoms/typography/Text"
import { DatePicker } from "../../molecules/DatePicker"
import { HorizontalSelectInput } from "../../molecules/HorizontalSelectInput"
import { type UIPauseReason } from "../../../ports/primary/ui/types"

/** Ce que le champ affiche tant que la date n'est pas choisie — le mot du legacy. */
const DATE_PLACEHOLDER = "Sélectionner"

export type PauseDatesStepProps = {
    /** `YYYY-MM-DD`, ou `null` tant que rien n'est choisi. */
    startDate: string | null
    onChangeStartDate: (next: string) => void
    startMinDate?: Date
    startMaxDate?: Date
    /** Le début ne se choisit pas du tout — la fenêtre estivale appartient au Programme d'été. */
    startDateDisabled?: boolean
    /** La limite atteinte, en une phrase courte. Absente → rien ne s'affiche. */
    limitNote?: string | null
    /** Ouvre l'aide, sous la limite. */
    onContactSupport?: () => void

    resumeDate: string | null
    onChangeResumeDate: (next: string) => void
    resumeMinDate?: Date
    resumeMaxDate?: Date

    /** « 21 jours » — absent tant que les deux dates ne sont pas posées. */
    durationLabel?: string | null
    /** La phrase qui refuse les dates choisies. Absente → rien ne s'affiche. */
    dateError?: string | null

    reasons: UIPauseReason[]
    selectedReason?: string | null
    onSelectReason: (value: string) => void

    testID?: string
}

export function PauseDatesStep(props: PauseDatesStepProps) {
    const {
        startDate,
        onChangeStartDate,
        startMinDate,
        startMaxDate,
        startDateDisabled = false,
        limitNote,
        onContactSupport,
        resumeDate,
        onChangeResumeDate,
        resumeMinDate,
        resumeMaxDate,
        durationLabel,
        dateError,
        reasons,
        selectedReason,
        onSelectReason,
        testID,
    } = props

    return (
        <Stack gap="5" testID={testID}>
            {limitNote != null && limitNote !== "" && (
                <AlertCard
                    severity="info"
                    message={limitNote}
                    actions={
                        onContactSupport != null && (
                            <TextButton label="Nous écrire" size="sm" onPress={onContactSupport} />
                        )
                    }
                />
            )}

            <DatePicker
                label="Date de début"
                value={startDate ?? ""}
                onChange={onChangeStartDate}
                placeholder={DATE_PLACEHOLDER}
                minDate={startMinDate}
                maxDate={startMaxDate}
                disabled={startDateDisabled}
            />

            <Stack gap="2">
                <DatePicker
                    label="Date de reprise"
                    value={resumeDate ?? ""}
                    onChange={onChangeResumeDate}
                    placeholder={DATE_PLACEHOLDER}
                    minDate={resumeMinDate}
                    maxDate={resumeMaxDate}
                    disabled={startDate == null}
                />
                {durationLabel != null && durationLabel !== "" && (
                    <Text size="text-sm" color="text-muted">
                        {`Durée : ${durationLabel}.`}
                    </Text>
                )}
                {dateError != null && dateError !== "" && (
                    <Banner message={dateError} color="danger" icon="exclamation-circle" tinted />
                )}
            </Stack>

            <HorizontalSelectInput
                label="Motif"
                items={reasons}
                getItemId={(reason) => reason.value}
                renderItem={(reason) => (
                    <ChoiceChip
                        variant="pill"
                        label={reason.label}
                        selected={reason.value === selectedReason}
                        onPress={() => onSelectReason(reason.value)}
                    />
                )}
            />
        </Stack>
    )
}
